import networkService from '../Network/NetworkManager';
import AppConstants from '../Constants/AppConstants';
import {convertPlanName} from '../utils/convertPlanName';

const APPURL = require('../Constants/URLConstants');

const PLAN_FEATURES_TYPE = 'tradly';

// Keep subscription loading single-flight. This protects the app if the active
// root and the legacy root are mounted during a transition or by a host shell.
let subscriptionRequest = null;

const getPlanLookupKey = plan =>
  plan?.unique_id || plan?.lookup_key || plan?.name || '';

const getSubscribedPlan = subData => {
  if (subData?.old_plan) {
    return subData.old_plan;
  }

  const plans = Array.isArray(subData?.plans) ? subData.plans : [];
  return (
    plans.find(plan => plan?.subscribed && !plan?.expired) ||
    plans.find(plan => plan?.subscribed) ||
    null
  );
};

const normalizeRestrictionKeys = restricted => {
  if (Array.isArray(restricted)) {
    return restricted.filter(Boolean);
  }

  if (!restricted || typeof restricted !== 'object') {
    return [];
  }

  return Object.keys(restricted).filter(key => restricted[key] === true);
};

const getFeaturePolicy = responseJson => {
  const candidates = [
    responseJson?.data?.policy,
    responseJson?.data?.plan_features,
    responseJson?.data?.features,
    responseJson?.policy,
    responseJson?.data,
    responseJson?.plan_features,
    responseJson,
  ];

  for (const candidate of candidates) {
    const policies = Array.isArray(candidate) ? candidate : [candidate];
    const policy = policies.find(
      entry =>
        entry &&
        typeof entry === 'object' &&
        (entry.allowed !== undefined ||
          entry.restricted !== undefined ||
          entry.limits !== undefined ||
          entry.caps !== undefined ||
          entry.entitlements !== undefined),
    );
    if (policy) {
      return policy;
    }
  }

  return null;
};

const setRestrictionState = (restrictedFeatures, normalizedPlanName) => {
  const restrictions = Array.from(new Set(restrictedFeatures || []));
  AppConstants.restrictedFeatureArray = restrictions;

  // A few legacy screens read accessControl directly instead of the shared
  // restrictedFeatureArray. Keep those screens on the same remote policy while
  // preserving the static map as the fallback when the API is unavailable.
  if (normalizedPlanName) {
    AppConstants.accessControl[normalizedPlanName] = restrictions;
  }
};

const applySubscriptionData = subData => {
  AppConstants.requiresPlanSelection = Boolean(subData?.requires_plan_selection);
  const subscribedPlan = getSubscribedPlan(subData);
  const unpaidInvoices = Number(subData?.unpaid_invoices || 0);

  AppConstants.subscribed = unpaidInvoices <= 0 ? AppConstants.subscribed : false;

  if (!subscribedPlan) {
    return null;
  }

  const lookupKey = getPlanLookupKey(subscribedPlan);
  const normalizedPlanName = convertPlanName(lookupKey);
  const legacyRestrictions = subscribedPlan.restricted_features || [];

  AppConstants.subscriptionLookupKey = lookupKey;
  AppConstants.plan_name = normalizedPlanName;
  setRestrictionState(legacyRestrictions, normalizedPlanName);

  if (
    lookupKey === 'community_monthly' ||
    (!subscribedPlan.expired && unpaidInvoices <= 0)
  ) {
    AppConstants.subscribed = true;
  }

  return {
    lookupKey,
    normalizedPlanName,
    legacyRestrictions,
  };
};

const loadPlanFeaturePolicy = async subscription => {
  if (!subscription?.lookupKey) {
    return null;
  }

  const path =
    `${APPURL.URLPaths.subscriptionPlanFeatures}?lookup_key=` +
    `${encodeURIComponent(subscription.lookupKey)}&type=${PLAN_FEATURES_TYPE}`;
  const responseJson = await networkService.networkCall(path, 'get', '');

  if (responseJson?.status !== true) {
    return null;
  }

  const policy = getFeaturePolicy(responseJson);
  if (!policy) {
    return null;
  }

  const restrictedFeatures = normalizeRestrictionKeys(policy.restricted);
  const normalizedPlanName = subscription.normalizedPlanName;

  const numericEntitlements = [policy.limits, policy.caps, policy.entitlements]
    .find(value => value && typeof value === 'object' && !Array.isArray(value)) || {};
  AppConstants.featureEntitlements = {
    ...numericEntitlements,
    ...(policy.allowed && typeof policy.allowed === 'object' ? policy.allowed : {}),
  };
  setRestrictionState(restrictedFeatures, normalizedPlanName);

  return policy;
};

export const loadSubscriptionEntitlements = () => {
  if (!subscriptionRequest) {
    subscriptionRequest = networkService
      .networkCall(APPURL.URLPaths.subscriptionsPlans, 'get')
      .then(responseJson => {
        if (responseJson?.status !== true) {
          return responseJson;
        }

        const subscription = applySubscriptionData(responseJson.data || {});
        return loadPlanFeaturePolicy(subscription).then(() => responseJson);
      })
      .catch(error => {
        // Permit a later retry if the request failed before returning an API
        // response, without creating duplicate requests while it is pending.
        subscriptionRequest = null;
        return error;
      });
  }

  return subscriptionRequest;
};

export const resetSubscriptionEntitlements = () => {
  subscriptionRequest = null;
  AppConstants.subscriptionLookupKey = '';
  AppConstants.featureEntitlements = {};
};
